<script setup lang="ts">
import GwBtn from '@/components/lib/GwBtn/GwBtn.vue'
import chip from '@/assets/icons/chip.svg'
import { PowerCredits } from '@/helpers/constants'
import GwTooltip from '@/components/lib/GwTooltip.vue'
import { useStore } from 'vuex'
import { computed } from 'vue'

const store = useStore()
const isMobile = computed(() => store.getters.globals_mobile)

interface Item {
  src: string
  credits: number
  price: string
  offerPercent?: string
  isRecommended: boolean
  maxHeight: number
}

defineProps<{ item: Item; onClick?: (item: Item) => void }>()
</script>

<template>
  <div
    class="gems-card"
    :class="{ 'gems-card--recommended': item.isRecommended }"
  >
    <div class="gems-card__wrapper">
      <img
        class="gems-card__img"
        :src="item.src"
        :style="{ maxHeight: `${item.maxHeight}px` }"
      />
    </div>

    <div
      class="d-flex align-center justify-center flex-column"
      style="gap: 4px; z-index: 30"
    >
      <span class="gems-card__badge">
        <template v-if="item.offerPercent"> {{ item.offerPercent }} Extra </template>
        <template v-else>
          <span> {{ $t('user_settings.credits_equivalent') }} </span>
          <GwTooltip
            :tooltip="$t('user_settings.credits_tooltip')"
            :placement="isMobile ? 'bottom-end' : 'bottom-start'"
            :hideDelay="300"
          />
        </template>
      </span>
      <div class="d-flex align-center ga-1">
        <span class="gems-card__icon-span">
          <component
            :is="chip"
            :style="{ color: 'var(--palette-text-primary)' }"
          />
        </span>
        <span
          class="gems-card__credits"
          style="color: var(--palette-text-primary)"
        >
          {{ item.credits.toLocaleString() }}&nbsp;{{ PowerCredits }}
        </span>
      </div>
    </div>

    <GwBtn
      :variant="item.isRecommended ? 'btn_primary-v2' : 'btn_default'"
      :text="item.price"
      style="height: 48px"
      @click="() => onClick?.(item)"
    >
      <template #default>
        <div class="gems-card__btn-text">
          {{ $t('user_settings.credits_buy') }} {{ item.price }}
        </div>
      </template>
    </GwBtn>
  </div>
</template>

<style lang="scss" scoped>
.gems-card {
  display: flex;
  text-align: center;
  justify-content: space-between;
  flex-direction: column;
  background: var(--palette-background-paper);
  padding: 16px;
  gap: 16px;
  border-radius: var(--radius-med);
  border: 1px solid rgba(224, 224, 224, 0.3);

  &--recommended {
    border: 2px solid var(--palette-primary-main);
  }

  &__wrapper {
    width: 100%;
    height: clamp(140px, 15vw, 100px);
    display: block;
    margin: 0 auto;
    position: relative;
  }

  &__img {
    position: absolute;
    bottom: -50px;
    z-index: 20;
    transform: translateX(-50%);
    width: 100%;
    height: clamp(160px, 18vw, 340px);
    object-fit: contain;
  }

  &__credits {
    font: var(--font-h6);
  }

  &__badge {
    font: var(--font-body2);
    color: #ffa726;
    padding: 4px 8px;
    border: 1px solid #ffa726;
    background-color: var(--palette-background-paper);
    border-radius: 100px;
    display: inline-block;
    text-align: center;
    margin: 0 auto;
    display: flex;
    gap: 4px;
  }

  &__btn-text {
    text-align: center;
    width: 100%;
    font-size: 16px;
    font-weight: var(--text-weight);
  }

  &__icon-span {
    width: 24px;
    height: 24px;
  }
}

@media (max-width: 720px) {
  .gems-card {
    height: 220px;
    min-width: unset;
    max-width: unset;
    &__badge {
      font-size: 12px;
    }
    &__credits {
      font-size: 17px;
    }
    &__icon-span {
      width: 16px;
      height: 16px;
    }
  }
  .gems-card :deep(button) {
    height: 40px !important;
  }
}
</style>
